// src/pages/shop/ShopPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getProducts } from '../../services/api';
import {
  FaHome,
  FaFilter,
  FaChevronDown,
  FaStar,
  FaShoppingBag,
  FaTimes
} from 'react-icons/fa';
import './ShopPage.css';

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(['all']);
  const [categoryLabels, setCategoryLabels] = useState({ all: 'All' });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeFilters, setActiveFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [availableSizes, setAvailableSizes] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sortMode, setSortMode] = useState('default');

  const [priceBounds, setPriceBounds] = useState({ min: 0, max: 10000 });
  const [priceSliderValue, setPriceSliderValue] = useState({ min: 0, max: 10000 });

  const [priceSortMode, setPriceSortMode] = useState('featured');
  const [showSortMenu, setShowSortMenu] = useState(false);

  const [hoveredId, setHoveredId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [sizesCache, setSizesCache] = useState({});

  const loadSizesForProduct = async (productId) => {
    if (sizesCache[productId]) return;
    setSizesCache(prev => ({ ...prev, [productId]: 'loading' }));
    try {
      const response = await fetch(`https://demo-site.creativepulse.pk/demo-api/admin/get-product-guide.php?product_id=${productId}`);
      const data = await response.json();
      const rows = data?.guide?.guide_rows;
      if (data.success && Array.isArray(rows) && rows.length > 0) {
        const sizes = rows.map(r => r.size).filter(Boolean);
        setSizesCache(prev => ({ ...prev, [productId]: sizes.length > 0 ? sizes : 'none' }));
      } else {
        setSizesCache(prev => ({ ...prev, [productId]: 'none' }));
      }
    } catch (error) {
      console.error('Error loading sizes:', error);
      setSizesCache(prev => ({ ...prev, [productId]: 'none' }));
    }
  };

  const handleHoverStart = (productId) => {
    setHoveredId(productId);
    loadSizesForProduct(productId);
  };

  const handleToggleExpand = (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    if (expandedId === productId) {
      setExpandedId(null);
    } else {
      setExpandedId(productId);
      loadSizesForProduct(productId);
    }
  };

  // This is the actual fix for "not adding to cart" — ShopPage.jsx never
  // had this function at all; the old Quick Add button had no onClick.
  const addToCart = (product, size = null) => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const existingItem = cart.find(item => item.id === product.id && item.size === size);
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({ ...product, size, quantity: 1 });
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('openSideCart'));
    setExpandedId(null);
  };

  const SORT_OPTIONS = [
    { value: 'featured', label: 'Featured' },
    { value: 'price-low-high', label: 'Price: Low to High' },
    { value: 'price-high-low', label: 'Price: High to Low' },
    { value: 'newest', label: 'Newest First' },
    { value: 'name-a-z', label: 'Name: A to Z' },
  ];

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSortMode(params.get('sort') || 'default');
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');

    // If a valid category is in the URL, select it
    if (categoryParam && categories.includes(categoryParam)) {
      setSelectedCategory(categoryParam);
      const categoryDisplayName = getCategoryDisplayName(categoryParam);

      setActiveFilters(prev => {
        // Completely strip out ALL old category filters first
        const nonCategoryFilters = prev.filter(f => {
          // Keep only filters that are NOT categories (e.g., keep size/price if you had tags, though you don't here)
          return !Object.values(categoryLabels).includes(f) && !categories.slice(1).includes(f.toLowerCase());
        });
        // Then add the new one
        return [...nonCategoryFilters, categoryDisplayName];
      });
    } else if (!categoryParam) {
      // If no category in URL, default to 'all' and strip all category filters
      setSelectedCategory('all');
      setActiveFilters(prev => {
        return prev.filter(f => {
          return !Object.values(categoryLabels).includes(f) && !categories.slice(1).includes(f.toLowerCase());
        });
      });
    }
  }, [location.search, categories, categoryLabels]);

  useEffect(() => {
    loadAttributes();
    loadCategories();
    loadProducts();
  }, []);

  useEffect(() => {
    if (products.length > 0) {
      const prices = products.map(p => parseFloat(p.price)).filter(p => !isNaN(p));
      if (prices.length > 0) {
        const min = Math.floor(Math.min(...prices));
        const max = Math.ceil(Math.max(...prices));
        setPriceBounds({ min, max });
        setPriceSliderValue(prev =>
          prev.min === 0 && prev.max === 10000 ? { min, max } : prev
        );
      }
    }
  }, [products]);

  useEffect(() => {
    filterProducts();
    // eslint-disable-next-line
  }, [products, selectedCategory, selectedSizes, priceSliderValue, priceBounds, sortMode]);

  const loadAttributes = async () => {
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/product-attributes.php');
      const data = await response.json();

      if (data.success) {
        setAvailableSizes(data.sizes || []);
      } else {
        setAvailableSizes(['S', 'M', 'L', 'XL', 'XXL']);
      }
    } catch (error) {
      console.error('Error loading attributes:', error);
      setAvailableSizes(['S', 'M', 'L', 'XL', 'XXL']);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/public/get-settings.php');
      const data = await response.json();

      if (data.success && data.settings) {
        let categoriesArray = ['all'];
        let labelsObj = { all: 'All' };

        if (data.settings.categories) {
          try {
            const parsed = JSON.parse(data.settings.categories);
            categoriesArray = ['all', ...parsed];
          } catch (e) {
            console.error('Error parsing categories:', e);
          }
        }

        if (data.settings.category_labels) {
          try {
            const parsedLabels = JSON.parse(data.settings.category_labels);
            labelsObj = { all: 'All', ...parsedLabels };
          } catch (e) {
            console.error('Error parsing category labels:', e);
          }
        }

        setCategories(categoriesArray);
        setCategoryLabels(labelsObj);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await getProducts();

      if (data.success && data.products && data.products.length > 0) {
        const productsWithVariants = await Promise.all(
          data.products.map(async (product) => {
            try {
              const variantResponse = await fetch(
                `https://demo-site.creativepulse.pk/demo-api/public/product-variants.php?product_id=${product.id}`
              );
              const variantData = await variantResponse.json();
              return {
                ...product,
                variants: variantData.success ? variantData.variants : []
              };
            } catch (error) {
              console.error(`Error loading variants for product ${product.id}:`, error);
              return { ...product, variants: [] };
            }
          })
        );

        setProducts(productsWithVariants);
        setFilteredProducts(productsWithVariants);
      } else {
        setProducts([]);
        setFilteredProducts([]);
        toast.info('No products found in database.');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
      setFilteredProducts([]);
      toast.error('Failed to load products. Please check API connection.');
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => {
        const productCategory = product.category?.toLowerCase().replace(/[& ]/g, '-') || '';
        return productCategory === selectedCategory ||
          product.category?.toLowerCase().replace(/[& ]/g, '-') === selectedCategory;
      });
    }

    if (selectedSizes.length > 0) {
      filtered = filtered.filter(product => {
        if (!product.variants || product.variants.length === 0) return false;
        return product.variants.some(variant =>
          variant.size && selectedSizes.includes(variant.size)
        );
      });
    }

    if (priceSliderValue.min > priceBounds.min || priceSliderValue.max < priceBounds.max) {
      filtered = filtered.filter(product => {
        const productPrice = parseFloat(product.price);
        return productPrice >= priceSliderValue.min && productPrice <= priceSliderValue.max;
      });
    }

    if (sortMode === 'new-arrivals') {
      filtered = filtered.filter(p => Number(p.is_new_arrival) === 1);
    } else if (sortMode === 'sale') {
      filtered = filtered.filter(p => Number(p.is_on_sale) === 1);
    }

    if (priceSortMode === 'price-low-high') {
      filtered = [...filtered].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    } else if (priceSortMode === 'price-high-low') {
      filtered = [...filtered].sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    } else if (priceSortMode === 'newest') {
      filtered = [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (priceSortMode === 'name-a-z') {
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }
    // 'featured' keeps whatever order filtering already produced — no re-sort

    setFilteredProducts(filtered);
  };

  useEffect(() => {
    filterProducts();
    // eslint-disable-next-line
  }, [products, selectedCategory, selectedSizes, priceSliderValue, priceBounds, sortMode, priceSortMode]);

  const handleCategoryChange = (category) => {
    setShowFilters(false);
    setShowMobileFilters(false);

    const params = new URLSearchParams(location.search);

    // If clicking the already-selected category (and it's not "All"), unselect it and go back to "All"
    if (selectedCategory === category && category !== 'all') {
      params.delete('category');
      params.delete('sort');
      navigate(`/shop${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
    }
    else {
      if (category === 'all') {
        params.delete('category');
        params.delete('sort');
      } else {
        params.set('category', category);
        params.delete('sort');
      }
      navigate(`/shop${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
    }
  };

  const handleSortPillClick = (mode) => {
    const params = new URLSearchParams(location.search);
    if (sortMode === mode) {
      params.delete('sort');
    } else {
      params.set('sort', mode);
    }
    navigate(`/shop${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
  };

  const handleSizeToggle = (size) => {
    setSelectedSizes(prev =>
      prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]
    );
  };

  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSelectedSizes([]);
    setPriceSliderValue(priceBounds);
    setActiveFilters([]);

    const params = new URLSearchParams(location.search);
    params.delete('category');
    navigate(`/shop${params.toString() ? '?' + params.toString() : ''}`, { replace: true });
  };

  const removeFilter = (filter) => {
    const categoryKeys = categories.slice(1);
    const matchedCategory = categoryKeys.find(key =>
      getCategoryDisplayName(key) === filter
    );

    if (matchedCategory) {
      handleCategoryChange('all');
    } else {
      setActiveFilters(prev => prev.filter(f => f !== filter));
    }
  };

  const getCategoryDisplayName = (categoryKey) => {
    return categoryLabels[categoryKey] || categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
  };

  const getCategoryForProduct = (product) => {
    if (product.category) {
      const categoryKey = product.category.toLowerCase().replace(/[& ]/g, '-');
      return categoryLabels[categoryKey] || product.category;
    }
    return 'Uncategorized';
  };

  const hasActivePriceFilter = priceSliderValue.min > priceBounds.min || priceSliderValue.max < priceBounds.max;

  return (
    <div className="shop-page">
      <div className="shop-container">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/" className="breadcrumb-link">
            <FaHome className="breadcrumb-icon" /> Home
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Shop</span>
        </nav>

        <header className="shop-header">
          <h1 className="shop-title">
            {sortMode === 'new-arrivals' ? 'New Arrivals' : sortMode === 'sale' ? 'Shop Sale' : 'Shop All'}
          </h1>
          <p className="shop-subtitle">
            {sortMode === 'new-arrivals'
              ? 'Fresh styles just added to the collection.'
              : sortMode === 'sale'
                ? 'Great picks at our best prices.'
                : 'Browse our full collection.'}
          </p>
        </header>

        <div className="category-pills-container">
          <div className="category-pills">
            {categories.map(category => (
              <button
                key={category}
                className={`category-pill ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryChange(category)}
              >
                {getCategoryDisplayName(category)}
              </button>
            ))}
            {/* 
            <button
              className={`category-pill ${sortMode === 'sale' ? 'active' : ''}`}
              onClick={() => handleSortPillClick('sale')}
            >
              Sale
            </button>
            <button
              className={`category-pill ${sortMode === 'new-arrivals' ? 'active' : ''}`}
              onClick={() => handleSortPillClick('new-arrivals')}
            >
              New Arrivals
            </button>
            */}
          </div>
        </div>

        {(activeFilters.length > 0 || selectedSizes.length > 0 || hasActivePriceFilter) && (
          <div className="active-filters">
            {activeFilters.map(filter => (
              <span key={filter} className="filter-tag">
                {filter}
                <button onClick={() => removeFilter(filter)} className="filter-remove">
                  <FaTimes />
                </button>
              </span>
            ))}
            {selectedSizes.map(size => (
              <span key={size} className="filter-tag">
                Size: {size}
                <button onClick={() => handleSizeToggle(size)} className="filter-remove">
                  <FaTimes />
                </button>
              </span>
            ))}
            {hasActivePriceFilter && (
              <span className="filter-tag">
                PKR {priceSliderValue.min.toLocaleString()} – {priceSliderValue.max.toLocaleString()}
                <button onClick={() => setPriceSliderValue(priceBounds)} className="filter-remove">
                  <FaTimes />
                </button>
              </span>
            )}
            {(selectedCategory !== 'all' || selectedSizes.length > 0 || hasActivePriceFilter) && (
              <button className="clear-all" onClick={clearAllFilters}>
                Clear All
              </button>
            )}
          </div>
        )}

        <div className="product-controls">
          <p className="product-count">
            <span className="count-number">{filteredProducts.length}</span> Products
          </p>
          <div className="controls-right">
            <button className="filter-toggle-btn" onClick={() => setShowMobileFilters(true)}>
              <FaFilter /> Filters
            </button>
            <div className="sort-dropdown">
              <button className="sort-btn" onClick={() => setShowSortMenu(!showSortMenu)}>
                <span className="sort-label">Sort:</span> {SORT_OPTIONS.find(o => o.value === priceSortMode)?.label}
                <FaChevronDown className="sort-icon" />
              </button>
              {showSortMenu && (
                <>
                  <div className="sort-menu-overlay" onClick={() => setShowSortMenu(false)}></div>
                  <div className="sort-menu">
                    {SORT_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        className={`sort-menu-item ${priceSortMode === option.value ? 'active' : ''}`}
                        onClick={() => {
                          setPriceSortMode(option.value);
                          setShowSortMenu(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="shop-layout">
          <aside className="shop-sidebar desktop-sidebar">
            <div className="sidebar-content">
              <div className="filter-section">
                <h4 className="filter-title">Price Range</h4>
                <div className="price-slider">
                  <div className="price-slider-values">
                    <span>PKR {priceSliderValue.min.toLocaleString()}</span>
                    <span>PKR {priceSliderValue.max.toLocaleString()}</span>
                  </div>
                  <div className="price-slider-track-wrapper">
                    <input
                      type="range"
                      min={priceBounds.min}
                      max={priceBounds.max}
                      value={priceSliderValue.min}
                      onChange={(e) => {
                        const val = Math.min(Number(e.target.value), priceSliderValue.max - 1);
                        setPriceSliderValue(prev => ({ ...prev, min: val }));
                      }}
                      className="price-slider-input price-slider-min"
                    />
                    <input
                      type="range"
                      min={priceBounds.min}
                      max={priceBounds.max}
                      value={priceSliderValue.max}
                      onChange={(e) => {
                        const val = Math.max(Number(e.target.value), priceSliderValue.min + 1);
                        setPriceSliderValue(prev => ({ ...prev, max: val }));
                      }}
                      className="price-slider-input price-slider-max"
                    />
                  </div>
                </div>
              </div>

              <div className="filter-section">
                <h4 className="filter-title">Size</h4>
                <div className="size-grid">
                  {availableSizes.length > 0 ? (
                    availableSizes.map(size => (
                      <button
                        key={size}
                        className={`size-btn ${selectedSizes.includes(size) ? 'active' : ''}`}
                        onClick={() => handleSizeToggle(size)}
                      >
                        {size}
                      </button>
                    ))
                  ) : (
                    <p className="no-attributes">No sizes available</p>
                  )}
                </div>
              </div>
            </div>
          </aside>

          <aside className={`shop-sidebar mobile-sidebar ${showMobileFilters ? 'active' : ''}`}>
            <div className="sidebar-header">
              <h3>Filters</h3>
              <button className="close-sidebar" onClick={() => setShowMobileFilters(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="sidebar-content">
              <div className="filter-section">
                <h4 className="filter-title">Price Range</h4>
                <div className="price-slider">
                  <div className="price-slider-values">
                    <span>PKR {priceSliderValue.min.toLocaleString()}</span>
                    <span>PKR {priceSliderValue.max.toLocaleString()}</span>
                  </div>
                  <div className="price-slider-track-wrapper">
                    <input
                      type="range"
                      min={priceBounds.min}
                      max={priceBounds.max}
                      value={priceSliderValue.min}
                      onChange={(e) => {
                        const val = Math.min(Number(e.target.value), priceSliderValue.max - 1);
                        setPriceSliderValue(prev => ({ ...prev, min: val }));
                      }}
                      className="price-slider-input price-slider-min"
                    />
                    <input
                      type="range"
                      min={priceBounds.min}
                      max={priceBounds.max}
                      value={priceSliderValue.max}
                      onChange={(e) => {
                        const val = Math.max(Number(e.target.value), priceSliderValue.min + 1);
                        setPriceSliderValue(prev => ({ ...prev, max: val }));
                      }}
                      className="price-slider-input price-slider-max"
                    />
                  </div>
                </div>
              </div>

              <div className="filter-section">
                <h4 className="filter-title">Size</h4>
                <div className="size-grid">
                  {availableSizes.map(size => (
                    <button
                      key={size}
                      className={`size-btn ${selectedSizes.includes(size) ? 'active' : ''}`}
                      onClick={() => handleSizeToggle(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              <button className="apply-filters-btn" onClick={() => setShowMobileFilters(false)}>
                Apply Filters
              </button>
            </div>
          </aside>

          {showMobileFilters && (
            <div className="mobile-overlay" onClick={() => setShowMobileFilters(false)}></div>
          )}

          <main className="shop-main">
            {loading ? (
              <div className="">
                <div className=""></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="no-products">
                <h3>No products found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              <div className="products-grid">
                {filteredProducts.map(product => {
                  let displayPrice = parseFloat(product.price);
                  let displaySalePrice = product.sale_price ? parseFloat(product.sale_price) : null;

                  if (product.variants && product.variants.length > 0) {
                    const prices = product.variants.map(v => parseFloat(v.price));
                    displayPrice = Math.min(...prices);
                    const salePrices = product.variants
                      .filter(v => v.sale_price)
                      .map(v => parseFloat(v.sale_price));
                    if (salePrices.length > 0) {
                      displaySalePrice = Math.min(...salePrices);
                    }
                  }

                  const sizeState = sizesCache[product.id];
                  const hasRealSizes = Array.isArray(sizeState) && sizeState.length > 0;
                  const isRevealed = hoveredId === product.id || expandedId === product.id;
                  const isExpanded = expandedId === product.id;
                  const isOutOfStock = !product.stock || parseInt(product.stock) <= 0;

                  return (
                    <div
                      key={product.id}
                      className="product-card"
                      onMouseEnter={() => !isOutOfStock && handleHoverStart(product.id)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <Link to={`/product/${product.id}`} className="product-link">
                        <div className="product-image-wrapper">
                          <img
                            src={product.image || '/assets/placeholder.jpg'}
                            alt={product.name}
                            className="product-image"
                          />
                          {(displaySalePrice && displaySalePrice < displayPrice) && (
                            <span className="product-tag sale">Sale</span>
                          )}

                          {/* Out-of-Stock overlay */}
                          {isOutOfStock && (
                            <span className="out-of-stock-badge">Out of Stock</span>
                          )}

                          {!isOutOfStock && (
                            <button
                              type="button"
                              className="mobile-expand-btn"
                              onClick={(e) => handleToggleExpand(e, product.id)}
                              aria-label={isExpanded ? 'Close' : 'Show sizes'}
                            >
                              {isExpanded ? <FaTimes /> : <span className="plus-icon">+</span>}
                            </button>
                          )}

                          {!isOutOfStock && (
                            <div className={`quick-add-overlay ${isRevealed ? 'visible' : ''}`}>
                              {hasRealSizes ? (
                                <>
                                  <span className="quick-add-label"></span>
                                  <div className="quick-add-sizes">
                                    {sizeState.map(size => (
                                      <button
                                        key={size}
                                        type="button"
                                        className="quick-add-size-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          addToCart(product, size);
                                        }}
                                      >
                                        {size}
                                      </button>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="quick-add-plain-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    addToCart(product);
                                  }}
                                >
                                  <FaShoppingBag /> Quick Add
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="product-category">{getCategoryForProduct(product)}</p>
                        <h3 className="product-name">{product.name}</h3>
                      </Link>
                      <div className="product-price">
                        <span className="current-price">
                          {displaySalePrice ? `PKR ${displaySalePrice.toLocaleString()}` : `PKR ${displayPrice.toLocaleString()}`}
                        </span>
                        {displaySalePrice && (
                          <span className="original-price">PKR {displayPrice.toLocaleString()}</span>
                        )}
                      </div>
                      <div className="product-rating">
                        <FaStar className="star-icon" />
                        <span className="rating-value">{product.rating || '4.5'}</span>
                        <span className="rating-count">({product.review_count || '0'})</span>
                      </div>

                      {/* This button previously had no onClick at all — that was the
                        entire bug. Desktop keeps it as a real, always-visible button;
                        mobile also gets the hover-overlay's tap-to-expand via the "+" above. */}
                      <button
                        className="quick-add-btn"
                        disabled={isOutOfStock}
                        onClick={(e) => {
                          e.preventDefault();
                          if (isOutOfStock) return;
                          if (hasRealSizes) {
                            handleToggleExpand(e, product.id);
                          } else {
                            addToCart(product);
                          }
                        }}
                      >
                        <FaShoppingBag /> {isOutOfStock ? 'Out of Stock' : 'Quick Add'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;